import { Box, Button } from '@mui/material';
import useAppName from '../../../hooks/useAppName';
import { isAppleMobileDevice } from '../../../utils/device';

/**
 * Renders a button that opens a link to the application's store page either on the Apple App Store or Google Play Store,
 * depending on the platform the app is being accessed from. The button dynamically adjusts its appearance and link based
 * on the environment settings.
 *
 * Official buttons guidelines:
 * - Apple: https://developer.apple.com/app-store/marketing/guidelines/
 * - Google: https://play.google.com/intl/en_us/badges/
 *
 * @returns {JSX.Element} A button element that links to the respective store page.
 */
const OpenStoreButton = () => {
  const appName = useAppName();
  const { isClockingApp } = window.env;
  const isAppleDevice = isAppleMobileDevice();

  // Decide which badge and URL to use based on the operating system
  const src = isAppleDevice ? 'static/apple-store-badge.svg' : 'static/google-play-badge.svg';
  let appleUrl = 'https://apps.apple.com/fr/app/comète-link/id1608699303';
  let androidUrl = 'https://play.google.com/store/apps/details?id=fr.aexae.comete';

  // Adjust the URLs for the "Comète On Time" app if needed
  if (isClockingApp) {
    appleUrl = 'https://apps.apple.com/us/app/com%C3%A8te-on-time/id6445984894';
    androidUrl = 'https://play.google.com/store/apps/details?id=fr.aexae.comete_pointages';
  }

  // Choose the correct URL based on the OS
  const href = isAppleDevice ? appleUrl : androidUrl;

  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener"
      size="large"
      variant="contained"
      sx={styleButton(isAppleDevice)}
    >
      <Box component="img" src={src} alt={`Logo ${appName}`} />
    </Button>
  );
};

/**
 * Returns the style object for the store button, adjusting for different OS-specific button designs.
 *
 * @param {boolean} isAppleDevice - Indicates if the device is an Apple mobile device.
 * @returns {Object} Style configuration object for the button.
 */
const styleButton = (isAppleDevice) => ({
  borderRadius: isAppleDevice ? 3.25 : 2.25,
  width: '60%',
  maxWidth: 300,
  p: '0 !important',
  backgroundColor: 'initial !important',
  img: {
    height: 'auto',
    width: '100%'
  }
});

export default OpenStoreButton;
